import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine,
  RadarChart, Radar, PolarGrid, PolarAngleAxis as RawPolarAngleAxis, PolarRadiusAxis as RawPolarRadiusAxis,
} from 'recharts';
import {
  DEMO_PATIENTS, DEMO_EVIDENCE_EVENTS, DEMO_CONSENT_RECORDS, DEMO_IP_LOG,
  DEMO_SAMPLE_LOG, DEMO_PROTOCOL_DEVIATIONS, DEMO_PATIENT_BIO_STATES,
  DEMO_DECISION_TWINS_V2, DEMO_VOTING_RECORDS, DEMO_DECISION_TWIN_BIO_STATES,
  DEMO_SAE_EVENTS, DEMO_SUSAR_CLASSIFICATIONS, SITE_MAP,
} from '../api/demoData';
import Glossary, { GlossaryEntry } from '../components/Glossary';
import ComplianceBadges from '../components/ComplianceBadges';

// Workaround for Recharts typing issues
const PolarAngleAxis = RawPolarAngleAxis as any;
const PolarRadiusAxis = RawPolarRadiusAxis as any;

// ─── Glossary ───
const GLOSSARY: GlossaryEntry[] = [
  { term: 'Biological Risk Modifier', definition: 'Percentage deviation of patient biological state from their own baseline' },
  { term: 'Heartfulness Coherence Index', definition: 'Heart rate coherence from Heartfulness meditation \u2014 measures cardiac coherence and autonomic regulation' },
  { term: 'HRV', definition: 'Heart Rate Variability in milliseconds' },
  { term: 'Cortisol', definition: 'Stress hormone \u2014 elevated levels indicate chronic stress and immune suppression' },
  { term: 'CYP2D6', definition: 'Cytochrome P450 enzyme that metabolizes many drugs' },
  { term: 'HLA-B*57:01', definition: 'Genetic marker for drug hypersensitivity reactions' },
  { term: 'PHQ-9', definition: 'Patient Health Questionnaire \u2014 depression screening score 0\u201327' },
  { term: 'GAD-7', definition: 'Generalized Anxiety Disorder scale score 0\u201321' },
  { term: 'AQI', definition: 'Air Quality Index \u2014 environmental pollution level' },
  { term: 'SHA-256', definition: 'Cryptographic hash ensuring tamper-evidence per 21 CFR Part 11' },
  { term: 'Decision Twin', definition: 'Immutable snapshot of complete evidence state at committee vote' },
  { term: 'Recording Delay', definition: 'Gap between effective and knowledge timestamps' },
];

// ─── Helpers ───
function riskColor(m: number) { return m <= 15 ? { bg: 'rgba(72,187,120,0.15)', text: '#48bb78', border: '#48bb78' } : m <= 30 ? { bg: 'rgba(236,201,75,0.15)', text: '#ecc94b', border: '#ecc94b' } : { bg: 'rgba(245,101,101,0.15)', text: '#f56565', border: '#f56565' }; }
function stressColor(s: string) { return s === 'green' ? '#48bb78' : s === 'amber' ? '#ecc94b' : '#f56565'; }
function aqiLabel(a: number) { return a <= 50 ? 'Good' : a <= 100 ? 'Moderate' : a <= 150 ? 'Unhealthy (Sensitive)' : 'Unhealthy'; }
function phq9Label(s: number) { return s <= 4 ? 'Minimal' : s <= 9 ? 'Mild' : s <= 14 ? 'Moderate' : s <= 19 ? 'Mod. Severe' : 'Severe'; }
function gad7Label(s: number) { return s <= 4 ? 'Minimal' : s <= 9 ? 'Mild' : s <= 14 ? 'Moderate' : 'Severe'; }

function fnvHash(input: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) { h ^= input.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  let r = '';
  for (let round = 0; round < 8; round++) { h ^= round * 0x9e3779b9; h = Math.imul(h, 0x01000193); r += (h >>> 0).toString(16).padStart(8, '0'); }
  return r;
}

const PI_MAP: Record<string, string> = { 'UCLA': 'Dr. Michael Torres', 'UCSF': 'Dr. Sarah Chen', 'Johns Hopkins': 'Dr. Priya Sharma' };
const ENROLLMENT: Record<string, string> = { 'SYN-001': '2026-01-14', 'SYN-002': '2026-01-14', 'SYN-003': '2026-01-14', 'SYN-004': '2026-01-20', 'SYN-005': '2026-01-20', 'SYN-006': '2026-01-20', 'SYN-007': '2026-01-25', 'SYN-008': '2026-01-25', 'SYN-009': '2026-01-25' };

// ─── Timeline types ───
type EvtCat = 'consent'|'visit'|'vitals'|'lab'|'ae'|'sae'|'pk'|'deviation'|'ip'|'sample'|'decision'|'bio';
interface TlEvent { id: string; timestamp: string; knowledgeTimestamp?: string; category: EvtCat; title: string; detail: string; person: string; site: string; flags: string[]; delayHours: number; hash: string; expanded?: Record<string, string>; }
const CAT: Record<EvtCat, { icon: string; color: string; label: string }> = {
  consent: { icon: '\uD83D\uDFE2', color: '#48bb78', label: 'Consent' }, visit: { icon: '\uD83D\uDD35', color: '#4299e1', label: 'Visit' },
  vitals: { icon: '\uD83D\uDFE1', color: '#ecc94b', label: 'Vital Signs' }, lab: { icon: '\uD83D\uDFE0', color: '#ed8936', label: 'Lab Result' },
  ae: { icon: '\uD83D\uDD34', color: '#f56565', label: 'AE Report' }, sae: { icon: '\uD83D\uDD34', color: '#c53030', label: 'SAE Report' },
  pk: { icon: '\uD83D\uDFE3', color: '#9f7aea', label: 'PK/PD Sample' }, deviation: { icon: '\u26AA', color: '#a0aec0', label: 'Deviation' },
  ip: { icon: '\uD83E\uDE7A', color: '#38b2ac', label: 'IP Dispense' }, sample: { icon: '\uD83E\uDDEA', color: '#63b3ed', label: 'Sample' },
  decision: { icon: '\uD83D\uDC99', color: '#2b6cb0', label: 'Decision Twin' }, bio: { icon: '\uD83D\uDCCB', color: '#b794f4', label: 'Bio State' },
};

function buildTimeline(pid: string): TlEvent[] {
  const ev: TlEvent[] = [];
  const site = SITE_MAP[pid]?.label || 'Unknown';
  const consent = DEMO_CONSENT_RECORDS.find((c) => c.patientId === pid);
  if (consent?.consented) ev.push({ id: `consent-${pid}`, timestamp: `${consent.consentDate}T09:00:00Z`, category: 'consent', title: 'Informed Consent Signed', detail: `ICF Form ${consent.formVersion} signed`, person: 'Site coordinator', site, flags: [], delayHours: 0, hash: fnvHash(`consent|${pid}|${consent.consentDate}`) });
  const bio = DEMO_PATIENT_BIO_STATES.find((b) => b.patientId === pid);
  if (bio) { const d = ENROLLMENT[pid] || '2026-01-14'; ev.push({ id: `bio-${pid}`, timestamp: `${d}T10:00:00Z`, category: 'bio', title: 'Biological State Baseline', detail: `CYP2D6: ${bio.genetic.cyp2d6}, HLA-B*57:01: ${bio.genetic.hlaB5701 ? 'Pos' : 'Neg'}, Cortisol: ${bio.dynamic.stressBiomarker.cortisolNmolL} nmol/L, PHQ-9: ${bio.dynamic.psychWellbeing.phq9}`, person: 'D3RI System', site, flags: bio.compositeRiskModifier > 30 ? ['HIGH RISK'] : [], delayHours: 0, hash: fnvHash(`bio|${pid}|${d}`), expanded: { CYP2D6: bio.genetic.cyp2d6, 'HLA-B*57:01': bio.genetic.hlaB5701 ? 'Positive' : 'Negative', BRCA: bio.genetic.brcaGermline ? 'Positive' : 'Negative', 'Target Engagement': `${bio.genetic.targetEngagement}%`, 'Risk Modifier': `+${bio.compositeRiskModifier}%` } }); }
  DEMO_EVIDENCE_EVENTS.filter((e) => e.subject === `Patient/${pid}`).forEach((e) => { const dh = (new Date(e.internalKnowledgeTimestamp).getTime() - new Date(e.effectiveTimestamp).getTime()) / 3600000; const c: EvtCat = e.eventType === 'SAE Report' ? 'sae' : e.eventType === 'AE Report' ? 'ae' : e.eventType === 'Lab Result' ? 'lab' : e.eventType === 'Vital Signs' ? 'vitals' : 'pk'; const f: string[] = []; if (dh > 12) f.push('CRITICAL DELAY'); else if (dh > 2) f.push('DELAYED'); const p = DEMO_PATIENTS.find((x) => x.id === pid); if (e.eventType === 'AE Report' && p?.dltFlag) f.push('DLT'); const sae = DEMO_SAE_EVENTS.find((s) => s.patient_id === pid && Math.abs(new Date(s.sae_date).getTime() - new Date(e.effectiveTimestamp).getTime()) < 86400000); if (sae) { const su = DEMO_SUSAR_CLASSIFICATIONS.find((sc) => sc.saeId === sae.sae_id); if (su?.classification === 'SUSAR') f.push('SUSAR'); } ev.push({ id: e.id, timestamp: e.effectiveTimestamp, knowledgeTimestamp: e.internalKnowledgeTimestamp, category: c, title: `${e.eventType} ${e.id}`, detail: `Content: ${e.content}`, person: 'Site staff', site, flags: f, delayHours: dh, hash: fnvHash(`${e.id}|${e.effectiveTimestamp}|${e.content}`) }); });
  DEMO_IP_LOG.filter((ip) => ip.patientId === pid).forEach((ip) => ev.push({ id: ip.id, timestamp: ip.timestamp, category: 'ip', title: `IP Dispense ${ip.id} \u2014 ${ip.dose}`, detail: `Batch ${ip.batchNumber}, ${ip.storageTempC.toFixed(1)}\u00B0C`, person: ip.dispensedBy, site: ip.site, flags: ip.status === 'Discrepancy' ? ['DISCREPANCY'] : [], delayHours: 0, hash: fnvHash(`${ip.id}|${ip.timestamp}`) }));
  DEMO_SAMPLE_LOG.filter((s) => s.patientId === pid).forEach((s) => { const f: string[] = []; if (s.delayMin > 60) f.push('LATE COLLECTION'); if (s.chainOfCustody !== 'Intact') f.push(s.chainOfCustody.toUpperCase()); if (s.status === 'Rejected') f.push('REJECTED'); if (s.status === 'Hemolyzed') f.push('HEMOLYZED'); ev.push({ id: s.id, timestamp: s.actualTime, category: 'sample', title: `${s.type} Sample ${s.id}`, detail: `${s.volume}, ${s.storageTempC}\u00B0C, turnaround ${s.turnaroundHours.toFixed(1)}h`, person: s.collectedBy, site: s.site, flags: f, delayHours: s.delayMin / 60, hash: fnvHash(`${s.id}|${s.actualTime}`) }); });
  DEMO_PROTOCOL_DEVIATIONS.filter((d) => d.patientId === pid).forEach((d) => ev.push({ id: d.id, timestamp: `${d.dateDiscovered}T12:00:00Z`, category: 'deviation', title: `${d.type} Deviation ${d.id}`, detail: d.description, person: 'QA Monitor', site, flags: d.type === 'Major' ? ['MAJOR'] : [], delayHours: 0, hash: fnvHash(`${d.id}|${d.dateDiscovered}`), expanded: d.linkedEvidenceId ? { 'Linked Evidence': d.linkedEvidenceId, Status: d.status } : { Status: d.status } }));
  DEMO_DECISION_TWIN_BIO_STATES.forEach((dtBio) => { const ps = dtBio.patientStates.find((p) => p.patientId === pid); if (!ps) return; const dt = DEMO_DECISION_TWINS_V2.find((t) => t.twin_id === dtBio.twinId); if (!dt) return; const vr = DEMO_VOTING_RECORDS.find((v) => v.twinId === dtBio.twinId); const f: string[] = []; if (!ps.acknowledged) f.push('BIO STATE UNACKNOWLEDGED'); if (ps.compositeRiskModifier > 30) f.push('HIGH RISK'); ev.push({ id: dtBio.twinId, timestamp: dt.captured_at, category: 'decision', title: `Decision Twin ${dt.twin_id}`, detail: `Evidence: ${dt.evidence_count}, Omissions: ${dt.omitted_count}, Votes: ${vr?.votes.map((v) => v.vote).join(', ')}`, person: dt.user_id, site: 'All Sites', flags: f, delayHours: 0, hash: dt.snapshot_hash, expanded: { 'M1 Safety': `${(dt.model_scores.m1_safety * 100).toFixed(0)}%`, 'M9 Go/No-Go': `${(dt.model_scores.m9_gonogo * 100).toFixed(0)}%`, 'Patient Risk': `+${ps.compositeRiskModifier}%`, 'Key Flags': ps.keyFlags.length > 0 ? ps.keyFlags.join(', ') : 'None', Acknowledged: ps.acknowledged ? 'Yes' : 'No', 'Vote Outcome': vr?.unanimous ? 'Unanimous' : 'Split' } }); });
  if (pid === 'SYN-002' && bio?.dynamic.dietaryInterference.flag) ev.push({ id: `dietary-${pid}`, timestamp: '2026-01-17T00:00:00Z', category: 'bio', title: 'Dietary Interference Flag', detail: bio.dynamic.dietaryInterference.detail, person: 'D3RI M17 Model', site, flags: ['HIGH RISK', 'DIETARY'], delayHours: 0, hash: fnvHash(`dietary|${pid}`) });
  ev.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  return ev;
}

// ─── Bio Profile Tab ───
function BioProfileTab({ patientId }: { patientId: string }) {
  const bio = DEMO_PATIENT_BIO_STATES.find((b) => b.patientId === patientId);
  const consent = DEMO_CONSENT_RECORDS.find((c) => c.patientId === patientId);

  if (!bio) return <div style={{ padding: 20, color: '#718096' }}>No biological data available for {patientId}. Biological state vectors are captured for Cohort 1 patients (SYN-001, SYN-002, SYN-003).</div>;

  const risk = riskColor(bio.compositeRiskModifier);
  const { genetic: g, dynamic: d } = bio;

  return (
    <div>
      {/* Consent */}
      <div style={st.bioSection}>
        <h4 style={st.bioSectionTitle}>Consent Verification</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ color: '#63b3ed', fontWeight: 600 }}>{patientId}</span>
          {consent?.consented ? (<><span style={st.greenBadge}>Consented</span><span style={{ color: '#718096', fontSize: 11 }}>{consent.consentDate} | Form {consent.formVersion}</span></>) : (<span style={st.redBadge}>Missing</span>)}
        </div>
      </div>

      {/* Static */}
      <div style={st.bioSection}>
        <h4 style={st.bioSectionTitle}>Static Layer \u2014 Genetic Profile</h4>
        <div style={st.bioGrid}>
          <Metric label="CYP2D6 Metabolizer" value={g.cyp2d6} color={g.cyp2d6 === 'Poor' ? '#f56565' : '#48bb78'} />
          <Metric label="HLA-B*57:01 Carrier" value={g.hlaB5701 ? 'Yes' : 'No'} color={g.hlaB5701 ? '#f56565' : '#48bb78'} />
          <Metric label="BRCA Germline" value={g.brcaGermline ? 'Positive' : 'Negative'} color={g.brcaGermline ? '#ecc94b' : '#48bb78'} />
          <div style={st.metricCard}><div style={st.metricLabel}>Target Engagement</div><div style={st.metricVal}>{g.targetEngagement}%</div><div style={st.barBg}><div style={{ height: 4, borderRadius: 2, width: `${g.targetEngagement}%`, backgroundColor: '#63b3ed' }} /></div></div>
        </div>
      </div>

      {/* Dynamic */}
      <div style={st.bioSection}>
        <h4 style={st.bioSectionTitle}>Dynamic Layer \u2014 Current Visit</h4>
        <div style={st.bioGrid}>
          <div style={{ ...st.metricCard, ...(d.dietaryInterference.flag ? { border: '1px solid #f56565', backgroundColor: 'rgba(245,101,101,0.08)' } : {}) }}>
            <div style={st.metricLabel}>Dietary Interference</div>
            <div style={{ ...st.metricVal, color: d.dietaryInterference.flag ? '#f56565' : '#48bb78', fontSize: 14 }}>{d.dietaryInterference.flag ? 'FLAG' : 'Clear'}</div>
            <div style={st.metricDetail}>{d.dietaryInterference.detail}</div>
          </div>
          <div style={st.metricCard}>
            <div style={st.metricLabel}>Heartfulness Coherence</div>
            {d.heartfulness.sessionsThisWeek > 0 ? (<><Row l="HRV" v={`${d.heartfulness.hrvMs} ms`} /><Row l="Coherence" v={`${d.heartfulness.coherenceRatio}%`} vc={d.heartfulness.coherenceRatio >= 60 ? '#48bb78' : d.heartfulness.coherenceRatio >= 40 ? '#ecc94b' : '#f56565'} /><Row l="Sessions" v={`${d.heartfulness.sessionsThisWeek}/wk`} /></>) : (<div style={{ color: '#f56565', fontSize: 14, fontWeight: 600, marginTop: 4 }}>No practice this week</div>)}
          </div>
          <Metric label="Stress Biomarker" value={`${d.stressBiomarker.cortisolNmolL} nmol/L`} color={stressColor(d.stressBiomarker.status)} />
          <div style={st.metricCard}><div style={st.metricLabel}>Sleep Quality</div><Row l="Hours" v={`${d.sleepQuality.hoursLastNight}h`} vc={d.sleepQuality.hoursLastNight >= 7 ? '#48bb78' : d.sleepQuality.hoursLastNight >= 6 ? '#ecc94b' : '#f56565'} /><Row l="Quality" v={`${d.sleepQuality.qualityScore}%`} /></div>
          <div style={st.metricCard}><div style={st.metricLabel}>Psychological Wellbeing</div><Row l="PHQ-9" v={`${d.psychWellbeing.phq9} (${phq9Label(d.psychWellbeing.phq9)})`} vc={d.psychWellbeing.phq9 <= 4 ? '#48bb78' : d.psychWellbeing.phq9 <= 9 ? '#ecc94b' : '#f56565'} /><Row l="GAD-7" v={`${d.psychWellbeing.gad7} (${gad7Label(d.psychWellbeing.gad7)})`} vc={d.psychWellbeing.gad7 <= 4 ? '#48bb78' : d.psychWellbeing.gad7 <= 9 ? '#ecc94b' : '#f56565'} /></div>
          <Metric label="Exercise" value={`${d.exerciseSteps.toLocaleString()} steps`} color={d.exerciseSteps >= 7000 ? '#48bb78' : d.exerciseSteps >= 4000 ? '#ecc94b' : '#f56565'} />
          <div style={st.metricCard}><div style={st.metricLabel}>Air Quality (AQI)</div><div style={{ ...st.metricVal, color: stressColor(d.airQuality.status) }}>{d.airQuality.aqi}</div><div style={st.metricDetail}>{aqiLabel(d.airQuality.aqi)}</div></div>
        </div>
      </div>

      {/* Composite */}
      <div style={{ ...st.compositeBox, backgroundColor: risk.bg, border: `1px solid ${risk.border}` }}>
        <div style={st.compositeLabel}>Composite Biological Risk Modifier</div>
        <div style={{ fontSize: 36, fontWeight: 800, color: risk.text, marginTop: 4 }}>+{bio.compositeRiskModifier}%</div>
        <div style={{ color: '#718096', fontSize: 12, marginTop: 2 }}>Deviation from patient baseline</div>
        <div style={st.barBg}><div style={{ height: 8, borderRadius: 4, width: `${Math.min(bio.compositeRiskModifier, 100)}%`, backgroundColor: risk.text }} /></div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, marginTop: 4 }}><span style={{ color: '#48bb78' }}>0%</span><span style={{ color: '#ecc94b' }}>15%</span><span style={{ color: '#f56565' }}>30%+</span></div>
      </div>
      <div style={{ color: '#4a5568', fontSize: 11, marginTop: 12, fontStyle: 'italic' }}>Last updated: {new Date(bio.lastUpdated).toLocaleString()}</div>
    </div>
  );
}

function Metric({ label, value, color }: { label: string; value: string; color?: string }) {
  return <div style={st.metricCard}><div style={st.metricLabel}>{label}</div><div style={{ ...st.metricVal, ...(color ? { color } : {}) }}>{value}</div></div>;
}
function Row({ l, v, vc }: { l: string; v: string; vc?: string }) {
  return <div style={st.metricRow}><span style={{ color: '#718096', fontSize: 11 }}>{l}</span><span style={{ ...st.metricVal, fontSize: 16, ...(vc ? { color: vc } : {}) }}>{v}</span></div>;
}

// ─── Risk score generator per event ───
function buildRiskTrace(events: TlEvent[], baselineRisk: number): { ts: number; risk: number; label: string; color: string; category: EvtCat; id: string }[] {
  let risk = baselineRisk;
  return events.map((e) => {
    // Adjust risk based on event type
    if (e.category === 'ae') risk = Math.min(100, risk + 11);
    else if (e.category === 'sae') risk = Math.min(100, risk + 14);
    else if (e.category === 'deviation') risk = Math.min(100, risk + 6);
    else if (e.category === 'bio' && e.flags.includes('DIETARY')) risk = Math.min(100, risk + 6);
    else if (e.category === 'sample' && (e.flags.includes('REJECTED') || e.flags.includes('HEMOLYZED'))) risk = Math.min(100, risk + 4);
    else if (e.category === 'lab' && e.delayHours > 12) risk = Math.min(100, risk + 3);
    else if (e.category === 'decision') risk = Math.max(0, risk - 3);
    else if (e.category === 'bio' && !e.flags.includes('DIETARY') && !e.flags.includes('HIGH RISK')) risk = Math.max(0, risk - 1);
    else risk = Math.max(0, risk + (Math.random() > 0.5 ? 1 : -1));
    const color = risk <= 30 ? '#48bb78' : risk <= 60 ? '#ecc94b' : '#f56565';
    return { ts: new Date(e.timestamp).getTime(), risk: Math.round(risk), label: e.title, color, category: e.category, id: e.id };
  });
}

function buildRadarData(pid: string, bio: typeof DEMO_PATIENT_BIO_STATES[0] | undefined) {
  if (!bio) {
    const def = 75;
    return { baseline: [
      { axis: 'Heartfulness', value: def }, { axis: 'Stress', value: def }, { axis: 'Sleep', value: def },
      { axis: 'Dietary', value: def }, { axis: 'Psych', value: def }, { axis: 'Activity', value: def },
    ], current: [
      { axis: 'Heartfulness', value: def }, { axis: 'Stress', value: def }, { axis: 'Sleep', value: def },
      { axis: 'Dietary', value: def }, { axis: 'Psych', value: def }, { axis: 'Activity', value: def },
    ]};
  }
  const d = bio.dynamic;
  const heartfulness = d.heartfulness.sessionsThisWeek > 0 ? d.heartfulness.coherenceRatio : 0;
  const stress = Math.max(0, 100 - (d.stressBiomarker.cortisolNmolL * 2.5));
  const sleep = d.sleepQuality.qualityScore;
  const dietary = d.dietaryInterference.flag ? 20 : 95;
  const psych = Math.max(0, 100 - (d.psychWellbeing.phq9 * 4));
  const activity = Math.min(100, (d.exerciseSteps / 10000) * 100);
  // Baseline — if patient is SYN-001 type, baseline ~= current; for SYN-002, baseline is first measurement
  const isHighRisk = bio.compositeRiskModifier > 30;
  const baseAdj = isHighRisk ? 0 : 10; // high risk patients baseline is same as current
  return {
    baseline: [
      { axis: 'Heartfulness', value: heartfulness + baseAdj }, { axis: 'Stress', value: stress + baseAdj },
      { axis: 'Sleep', value: sleep + baseAdj }, { axis: 'Dietary', value: Math.min(100, dietary + baseAdj) },
      { axis: 'Psych', value: psych + baseAdj }, { axis: 'Activity', value: activity + baseAdj },
    ],
    current: [
      { axis: 'Heartfulness', value: heartfulness }, { axis: 'Stress', value: stress },
      { axis: 'Sleep', value: sleep }, { axis: 'Dietary', value: dietary },
      { axis: 'Psych', value: psych }, { axis: 'Activity', value: activity },
    ],
  };
}

// ─── Bi-Temporal Timeline Tab (3 panels) ───
type FilterKey = 'all'|'safety'|'samples'|'decisions'|'bio';

function TimelineTab({ patientId }: { patientId: string }) {
  const [filter, setFilter] = useState<FilterKey>('all');
  const [playIdx, setPlayIdx] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const playIdxRef = useRef(-1);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const allEvents = useMemo(() => buildTimeline(patientId), [patientId]);
  const events = useMemo(() => allEvents.filter((e) => {
    if (filter === 'all') return true;
    if (filter === 'safety') return ['ae', 'sae', 'vitals'].includes(e.category);
    if (filter === 'samples') return ['sample', 'pk'].includes(e.category);
    if (filter === 'decisions') return e.category === 'decision';
    return e.category === 'bio';
  }), [allEvents, filter]);

  const bio = DEMO_PATIENT_BIO_STATES.find((b) => b.patientId === patientId);
  const baselineRisk = bio?.compositeRiskModifier || 10;

  const riskTrace = useMemo(() => buildRiskTrace(events, baselineRisk), [events, baselineRisk]);
  const radarData = useMemo(() => buildRadarData(patientId, bio), [patientId, bio]);

  // Playback
  useEffect(() => { playIdxRef.current = playIdx; }, [playIdx]);
  const stopPlayback = useCallback(() => { setIsPlaying(false); if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; } }, []);
  const startPlayback = useCallback(() => { playIdxRef.current = 0; setPlayIdx(0); setIsPlaying(true); }, []);

  useEffect(() => {
    if (!isPlaying) return;
    intervalRef.current = setInterval(() => {
      const next = playIdxRef.current + 1;
      if (next >= events.length) { stopPlayback(); return; }
      playIdxRef.current = next;
      setPlayIdx(next);
    }, 1000 / speed);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [isPlaying, speed, events.length, stopPlayback]);

  useEffect(() => { playIdxRef.current = -1; setPlayIdx(-1); stopPlayback(); }, [patientId, filter, stopPlayback]);

  // Visible data slices
  const visibleIdx = playIdx < 0 ? events.length - 1 : playIdx;
  const visibleRisk = riskTrace.slice(0, visibleIdx + 1);
  const currentEvt = events[visibleIdx];
  const currentMeta = currentEvt ? CAT[currentEvt.category] : null;

  // Decision twin reference lines
  const dtLines = events.filter((e) => e.category === 'decision').map((e) => ({ ts: new Date(e.timestamp).getTime(), label: e.id }));

  // Gap map data — events with knowledge timestamps
  const gapEvents = events.slice(0, visibleIdx + 1).filter((e) => e.knowledgeTimestamp).map((e) => ({
    effTs: new Date(e.timestamp).getTime(),
    knTs: new Date(e.knowledgeTimestamp!).getTime(),
    delayH: e.delayHours,
    id: e.id,
    color: e.delayHours <= 1 ? '#48bb78' : e.delayHours <= 6 ? '#ecc94b' : '#f56565',
    category: e.category,
  }));

  // Radar — interpolate current based on playback position
  const radarCurrent = useMemo(() => {
    if (playIdx < 0) return radarData.current;
    const progress = (visibleIdx + 1) / events.length;
    return radarData.baseline.map((b, i) => ({
      axis: b.axis,
      value: Math.round(b.value + (radarData.current[i].value - b.value) * progress),
    }));
  }, [playIdx, visibleIdx, events.length, radarData]);

  const radarMerged = radarData.baseline.map((b, i) => ({
    axis: b.axis, baseline: b.value, current: radarCurrent[i].value,
  }));

  const isWorseThanBaseline = radarCurrent.some((c, i) => c.value < radarData.baseline[i].value - 5);

  // Time domain
  const tMin = events.length > 0 ? new Date(events[0].timestamp).getTime() : 0;
  const tMax = events.length > 0 ? new Date(events[events.length - 1].timestamp).getTime() : 0;
  const currentTs = currentEvt ? new Date(currentEvt.timestamp).getTime() : tMax;

  return (
    <div>
      {/* PANEL 1 — EKG Risk Trace */}
      <div style={{ backgroundColor: '#1a202c', borderRadius: 8, padding: '16px 20px', marginBottom: 12, border: '1px solid #2d3748' }}>
        <h4 style={{ fontSize: 12, fontWeight: 600, color: '#cbd5e0', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>Risk Trace</h4>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={visibleRisk} margin={{ top: 10, right: 20, bottom: 10, left: 10 }}>
            <XAxis dataKey="ts" type="number" domain={[tMin, tMax]} tickFormatter={(v: number) => new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} tick={{ fill: '#718096', fontSize: 10 }} />
            <YAxis domain={[0, 100]} tick={{ fill: '#718096', fontSize: 10 }} width={35} tickFormatter={(v: number) => `${v}%`} />
            <Tooltip contentStyle={{ backgroundColor: '#171923', border: '1px solid #2d3748', color: '#e2e8f0', fontSize: 11 }} labelFormatter={(v: any) => new Date(v).toLocaleString()} formatter={(v: any, _: any, props: any) => [`${v}% — ${props.payload.label}`, 'Risk']} />
            {dtLines.map((dt) => <ReferenceLine key={dt.label} x={dt.ts} stroke="#ecc94b" strokeDasharray="3 3" label={{ value: dt.label, position: 'top', fill: '#ecc94b', fontSize: 9 }} />)}
            {currentEvt && <ReferenceLine x={currentTs} stroke="#63b3ed" strokeWidth={2} />}
            <Line type="monotone" dataKey="risk" stroke="#f56565" strokeWidth={2} dot={(props: any) => {
              const { cx, cy, payload } = props;
              if (!payload) return <circle key={props.key} cx={0} cy={0} r={0} />;
              const isCur = payload.ts === currentTs;
              const c = payload.color;
              if (payload.category === 'sample' && (events.find((e) => e.id === payload.id)?.flags.some((f: string) => f === 'REJECTED' || f === 'HEMOLYZED'))) return <text key={props.key} x={cx} y={cy} textAnchor="middle" fill="#f56565" fontSize={14} dy={4}>{'\u2716'}</text>;
              if (payload.category === 'deviation') return <text key={props.key} x={cx} y={cy} textAnchor="middle" fill="#ed8936" fontSize={12} dy={4}>{'\u25B2'}</text>;
              return <circle key={props.key} cx={cx} cy={cy} r={isCur ? 6 : 3} fill={c} stroke={isCur ? '#fff' : 'none'} strokeWidth={isCur ? 2 : 0} />;
            }} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
        <div style={{ display: 'flex', gap: 12, fontSize: 10, color: '#718096', marginTop: 4 }}>
          <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', backgroundColor: '#48bb78', marginRight: 4 }} />0-30%</span>
          <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', backgroundColor: '#ecc94b', marginRight: 4 }} />31-60%</span>
          <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', backgroundColor: '#f56565', marginRight: 4 }} />&gt;60%</span>
          <span style={{ marginLeft: 8 }}>{'\u25B2'} Deviation</span>
          <span>{'\u2716'} Rejected sample</span>
          <span style={{ color: '#ecc94b' }}>| Decision Twin</span>
        </div>
      </div>

      {/* Playback Controls */}
      <div style={{ backgroundColor: '#171923', borderRadius: 8, padding: '12px 16px', marginBottom: 12, border: '1px solid #2d3748' }}>
        <div style={st.controlsRow}>
          <button style={{ ...st.ctrlBtn, backgroundColor: isPlaying ? '#c53030' : '#2f855a' }} onClick={isPlaying ? stopPlayback : startPlayback}>{isPlaying ? '\u23F8 Pause' : '\u25B6 Play'}</button>
          <button style={st.ctrlBtn} onClick={() => { stopPlayback(); playIdxRef.current = 0; setPlayIdx(0); }}>|&lt; First</button>
          <button style={st.ctrlBtn} onClick={() => { stopPlayback(); playIdxRef.current = events.length - 1; setPlayIdx(events.length - 1); }}>Last &gt;|</button>
          <span style={{ color: '#718096', fontSize: 11, marginLeft: 4 }}>Speed:</span>
          {[0.5, 1, 2, 4].map((sp) => <button key={sp} style={{ ...st.ctrlBtn, backgroundColor: speed === sp ? '#2b6cb0' : '#2d3748' }} onClick={() => setSpeed(sp)}>{sp}x</button>)}
          <span style={st.counter}>{visibleIdx + 1} / {events.length}</span>
        </div>
        <input type="range" min={0} max={events.length - 1} value={visibleIdx} onChange={(e) => { stopPlayback(); const v = Number(e.target.value); playIdxRef.current = v; setPlayIdx(v); }} style={{ width: '100%', cursor: 'pointer', accentColor: '#63b3ed', marginTop: 8 }} />
        {currentEvt && currentMeta && (
          <div style={{ marginTop: 6, fontSize: 12, color: '#cbd5e0' }}>
            <span style={{ color: currentMeta.color, fontWeight: 700 }}>{currentMeta.icon} {currentMeta.label}</span>
            <span style={{ color: '#718096', marginLeft: 8 }}>{currentEvt.title} \u2014 {new Date(currentEvt.timestamp).toLocaleString()}</span>
          </div>
        )}
        <div style={{ ...st.filterRow, marginTop: 8, marginBottom: 0 }}>
          {(['all', 'safety', 'samples', 'decisions', 'bio'] as FilterKey[]).map((k) => (
            <button key={k} onClick={() => setFilter(k)} style={{ ...st.filterBtn, backgroundColor: filter === k ? '#2b6cb0' : '#2d3748', color: filter === k ? '#fff' : '#a0aec0' }}>{k === 'all' ? 'All' : k.charAt(0).toUpperCase() + k.slice(1)}</button>
          ))}
        </div>
      </div>

      {/* SWIM LANE — Event categories */}
      <div style={{ backgroundColor: '#1a202c', borderRadius: 8, padding: '16px 20px', marginBottom: 12, border: '1px solid #2d3748' }}>
        <h4 style={{ fontSize: 12, fontWeight: 600, color: '#cbd5e0', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>Event Swim Lanes</h4>
        {(() => {
          const lanes: EvtCat[] = ['consent', 'bio', 'vitals', 'lab', 'ae', 'sae', 'sample', 'ip', 'deviation', 'decision'];
          const tRange = tMax - tMin || 1;
          const visEvts = events.slice(0, visibleIdx + 1);
          return (
            <div>
              {lanes.map((lane) => {
                const laneEvts = visEvts.filter((e) => e.category === lane);
                if (laneEvts.length === 0 && visEvts.filter((e) => e.category === lane).length === 0 && events.filter((e) => e.category === lane).length === 0) return null;
                const meta = CAT[lane];
                return (
                  <div key={lane} style={{ display: 'flex', alignItems: 'center', marginBottom: 4, height: 24 }}>
                    <div style={{ width: 80, fontSize: 9, color: meta.color, fontWeight: 600, textTransform: 'uppercase', flexShrink: 0 }}>{meta.label}</div>
                    <div style={{ flex: 1, position: 'relative', height: 20, backgroundColor: '#171923', borderRadius: 3 }}>
                      {/* Current time marker */}
                      <div style={{ position: 'absolute', left: `${((currentTs - tMin) / tRange) * 100}%`, top: 0, bottom: 0, width: 1, backgroundColor: '#63b3ed', opacity: 0.5 }} />
                      {laneEvts.map((e) => {
                        const xPct = ((new Date(e.timestamp).getTime() - tMin) / tRange) * 100;
                        const isCur = currentEvt && e.id === currentEvt.id && e.timestamp === currentEvt.timestamp;
                        const hasFlag = e.flags.length > 0;
                        return (
                          <div key={e.id + e.timestamp} title={`${e.title} — ${new Date(e.timestamp).toLocaleString()}`} style={{
                            position: 'absolute', left: `${xPct}%`, top: '50%', transform: 'translate(-50%, -50%)',
                            width: isCur ? 12 : 8, height: isCur ? 12 : 8, borderRadius: '50%',
                            backgroundColor: hasFlag ? '#f56565' : meta.color,
                            border: isCur ? '2px solid #fff' : 'none',
                            boxShadow: isCur ? `0 0 6px ${meta.color}` : 'none',
                            zIndex: isCur ? 10 : 1,
                          }} />
                        );
                      })}
                    </div>
                  </div>
                );
              }).filter(Boolean)}
              {/* Date axis */}
              <div style={{ display: 'flex', marginLeft: 80, justifyContent: 'space-between', fontSize: 9, color: '#4a5568', marginTop: 2 }}>
                <span>{new Date(tMin).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                <span>{new Date((tMin + tMax) / 2).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                <span>{new Date(tMax).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
              </div>
            </div>
          );
        })()}
      </div>

      {/* PANEL 2 — Bi-Temporal Gap Map */}
      <div style={{ backgroundColor: '#1a202c', borderRadius: 8, padding: '16px 20px', marginBottom: 12, border: '1px solid #2d3748' }}>
        <h4 style={{ fontSize: 12, fontWeight: 600, color: '#cbd5e0', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>Bi-Temporal Gap Map</h4>
        <div style={{ display: 'flex', fontSize: 10, color: '#718096', marginBottom: 6, gap: 16 }}>
          <span>Top track: Effective Time (when it happened)</span>
          <span>Bottom track: Knowledge Time (when D3RI recorded it)</span>
        </div>
        {gapEvents.length > 0 ? (
          <div>
            {/* Normalized SVG — map timestamps to 0-1000 range */}
            {(() => {
              // Compute the full time range including knowledge timestamps
              const allTs = gapEvents.flatMap((g) => [g.effTs, g.knTs]);
              const svgMin = Math.min(tMin, ...allTs);
              const svgMax = Math.max(tMax, ...allTs);
              const svgRange = svgMax - svgMin || 1;
              const toX = (ts: number) => ((ts - svgMin) / svgRange) * 1000;
              const curX = toX(currentTs);
              return (
                <svg width="100%" height={140} viewBox="0 0 1000 140" preserveAspectRatio="xMidYMid meet" style={{ display: 'block' }}>
                  {/* Track backgrounds */}
                  <line x1={0} y1={30} x2={1000} y2={30} stroke="#2d3748" strokeWidth={2} />
                  <line x1={0} y1={110} x2={1000} y2={110} stroke="#2d3748" strokeWidth={2} />
                  {/* Track labels */}
                  <text x={5} y={22} fill="#718096" fontSize={9}>Effective</text>
                  <text x={5} y={128} fill="#718096" fontSize={9}>Knowledge</text>
                  {/* Current time indicator */}
                  <line x1={curX} y1={0} x2={curX} y2={140} stroke="#63b3ed" strokeWidth={1.5} strokeDasharray="4 3" opacity={0.7} />
                  {/* Gap lines and dots */}
                  {gapEvents.map((g) => {
                    const x1 = toX(g.effTs);
                    const x2 = toX(g.knTs);
                    const strokeW = g.delayH > 12 ? 3 : g.delayH > 6 ? 2 : 1.5;
                    return (
                      <g key={g.id}>
                        {/* Connecting line — the delay */}
                        <line x1={x1} y1={30} x2={x2} y2={110} stroke={g.color} strokeWidth={strokeW} opacity={0.9} />
                        {/* Effective dot (top) */}
                        <circle cx={x1} cy={30} r={5} fill={CAT[g.category]?.color || '#a0aec0'} stroke="#0d1117" strokeWidth={1} />
                        {/* Knowledge dot (bottom) */}
                        <circle cx={x2} cy={110} r={5} fill={CAT[g.category]?.color || '#a0aec0'} stroke="#0d1117" strokeWidth={1} />
                        {/* Delay label for big delays */}
                        {g.delayH > 6 && (
                          <text x={(x1 + x2) / 2 + 8} y={72} fill={g.color} fontSize={8} fontWeight="bold">{g.delayH.toFixed(0)}h</text>
                        )}
                      </g>
                    );
                  })}
                  {/* Date ticks */}
                  {[0, 0.25, 0.5, 0.75, 1].map((pct) => {
                    const ts = svgMin + svgRange * pct;
                    return <text key={pct} x={pct * 1000} y={138} fill="#4a5568" fontSize={7} textAnchor="middle">{new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</text>;
                  })}
                </svg>
              );
            })()}
            {/* Legend */}
            <div style={{ display: 'flex', gap: 16, fontSize: 10, color: '#718096', marginTop: 4 }}>
              <span><span style={{ display: 'inline-block', width: 20, height: 2, backgroundColor: '#48bb78', marginRight: 4, verticalAlign: 'middle' }} />&lt;1h delay</span>
              <span><span style={{ display: 'inline-block', width: 20, height: 2, backgroundColor: '#ecc94b', marginRight: 4, verticalAlign: 'middle' }} />1-6h delay</span>
              <span><span style={{ display: 'inline-block', width: 20, height: 3, backgroundColor: '#f56565', marginRight: 4, verticalAlign: 'middle' }} />&gt;6h delay</span>
              {gapEvents.some((g) => g.delayH > 12) && <span style={{ color: '#f56565', fontWeight: 600 }}>Critical gap detected</span>}
            </div>
          </div>
        ) : (
          <div style={{ color: '#4a5568', fontSize: 12, padding: 20, textAlign: 'center' }}>No bi-temporal events to display</div>
        )}
      </div>

      {/* PANEL 3 — Radial Biological State */}
      <div style={{ backgroundColor: '#1a202c', borderRadius: 8, padding: '16px 20px', marginBottom: 12, border: '1px solid #2d3748' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <h4 style={{ fontSize: 12, fontWeight: 600, color: '#cbd5e0', textTransform: 'uppercase', letterSpacing: 0.5 }}>Radial Biological State</h4>
          <span style={{ fontSize: 11, fontWeight: 600, color: isWorseThanBaseline ? '#f56565' : '#48bb78' }}>{isWorseThanBaseline ? 'Worse than baseline' : 'At or above baseline'}</span>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <RadarChart data={radarMerged} cx="50%" cy="50%" outerRadius="70%">
            <PolarGrid stroke="#2d3748" />
            <PolarAngleAxis dataKey="axis" tick={{ fill: '#a0aec0', fontSize: 11 }} />
            <PolarRadiusAxis domain={[0, 100]} tick={{ fill: '#4a5568', fontSize: 9 }} axisLine={false} />
            <Radar name="Baseline" dataKey="baseline" stroke="#4a5568" fill="#4a5568" fillOpacity={0.2} strokeDasharray="4 4" />
            <Radar name="Current" dataKey="current" stroke={isWorseThanBaseline ? '#f56565' : '#48bb78'} fill={isWorseThanBaseline ? '#f56565' : '#48bb78'} fillOpacity={0.3} strokeWidth={2} />
            <Tooltip contentStyle={{ backgroundColor: '#171923', border: '1px solid #2d3748', color: '#e2e8f0', fontSize: 11 }} />
          </RadarChart>
        </ResponsiveContainer>
        <div style={{ display: 'flex', gap: 16, justifyContent: 'center', fontSize: 10, color: '#718096' }}>
          <span><span style={{ display: 'inline-block', width: 12, height: 3, backgroundColor: '#4a5568', marginRight: 4, verticalAlign: 'middle' }} />Baseline</span>
          <span><span style={{ display: 'inline-block', width: 12, height: 3, backgroundColor: isWorseThanBaseline ? '#f56565' : '#48bb78', marginRight: 4, verticalAlign: 'middle' }} />Current</span>
        </div>
      </div>

      {/* Summary */}
      <div style={{ backgroundColor: '#1a202c', borderRadius: 8, padding: '16px 20px', border: '1px solid #2d3748' }}>
        <h3 style={{ fontSize: 12, fontWeight: 700, color: '#cbd5e0', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Summary</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8 }}>
          {[
            { l: 'Events', v: `${events.length}` },
            { l: 'Safety', v: `${allEvents.filter((e) => ['ae','sae'].includes(e.category)).length}`, c: '#f56565' },
            { l: 'Samples', v: `${allEvents.filter((e) => e.category === 'sample').length}` },
            { l: 'Deviations', v: `${allEvents.filter((e) => e.category === 'deviation').length}` },
            { l: 'DT Votes', v: `${allEvents.filter((e) => e.category === 'decision').length}` },
            { l: 'Unack Flags', v: `${allEvents.filter((e) => e.flags.includes('BIO STATE UNACKNOWLEDGED')).length}`, c: '#ecc94b' },
            { l: 'Max Delay', v: `${Math.max(0, ...allEvents.map((e) => e.delayHours)).toFixed(1)}h`, c: Math.max(0, ...allEvents.map((e) => e.delayHours)) > 12 ? '#f56565' : '#48bb78' },
            { l: 'Chain Breaks', v: `${allEvents.filter((e) => e.flags.some((f) => f.includes('TEMPERATURE') || f === 'BROKEN')).length}`, c: allEvents.some((e) => e.flags.some((f) => f.includes('TEMPERATURE'))) ? '#f56565' : '#48bb78' },
          ].map((s) => (
            <div key={s.l} style={{ backgroundColor: '#171923', borderRadius: 6, padding: '6px 10px', border: '1px solid #2d3748' }}>
              <div style={{ color: '#718096', fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.5 }}>{s.l}</div>
              <div style={{ color: s.c || '#e2e8f0', fontSize: 16, fontWeight: 700, marginTop: 1 }}>{s.v}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Main merged component ───
type Tab = 'bio' | 'timeline';

export default function PatientDigitalTwin() {
  const [selectedPatient, setSelectedPatient] = useState('SYN-002');
  const [tab, setTab] = useState<Tab>('bio');
  const patient = DEMO_PATIENTS.find((p) => p.id === selectedPatient);
  const siteInfo = SITE_MAP[selectedPatient];
  const bio = DEMO_PATIENT_BIO_STATES.find((b) => b.patientId === selectedPatient);
  const riskLvl = (bio?.compositeRiskModifier || 0) > 30 ? 'HIGH' : (bio?.compositeRiskModifier || 0) > 15 ? 'MODERATE' : 'LOW';

  return (
    <div style={st.container}>
      <Glossary entries={GLOSSARY} />
      <h2 style={st.title}>Patient Twin</h2>
      <p style={st.desc}>Unified biological profile and chronological timeline for each patient.</p>
      <ComplianceBadges badges={["ICH E6(R3)", "ALCOA", "MHRA Apr 2026", "UK CTR 2025"]} />

      {/* Patient selector */}
      <select value={selectedPatient} onChange={(e) => setSelectedPatient(e.target.value)} style={st.select}>
        {DEMO_PATIENTS.map((p) => <option key={p.id} value={p.id}>{p.id} \u2014 {p.cohort} \u2014 {SITE_MAP[p.id]?.label}</option>)}
      </select>

      {/* Patient header */}
      {patient && siteInfo && (
        <div style={st.headerCard}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <div>
              <span style={{ color: '#63b3ed', fontWeight: 800, fontSize: 20, marginRight: 10 }}>{patient.id}</span>
              <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, marginRight: 8, backgroundColor: siteInfo.color + '22', color: siteInfo.color, border: `1px solid ${siteInfo.color}` }}>{siteInfo.label}</span>
              <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, backgroundColor: '#2d3748', color: '#cbd5e0' }}>{patient.cohort}</span>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {patient.dltFlag && <span style={{ backgroundColor: '#f56565', color: '#fff', padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700 }}>DLT</span>}
              <span style={{ fontWeight: 700, fontSize: 13, color: riskLvl === 'HIGH' ? '#f56565' : riskLvl === 'MODERATE' ? '#ecc94b' : '#48bb78' }}>{riskLvl} RISK</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 16, marginTop: 10, color: '#a0aec0', fontSize: 12, flexWrap: 'wrap' }}>
            <span>PI: {PI_MAP[siteInfo.label]}</span><span>Enrolled: {ENROLLMENT[selectedPatient]}</span><span>Grade: {patient.grade}</span><span>AEs: {patient.aeCount}</span>
            {bio && <span>Risk: <b style={{ color: bio.compositeRiskModifier > 30 ? '#f56565' : bio.compositeRiskModifier > 15 ? '#ecc94b' : '#48bb78' }}>+{bio.compositeRiskModifier}%</b></span>}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={st.tabRow}>
        <button onClick={() => setTab('bio')} style={{ ...st.tabBtn, backgroundColor: tab === 'bio' ? '#2b6cb0' : '#1a202c', color: tab === 'bio' ? '#fff' : '#a0aec0', borderBottom: tab === 'bio' ? '2px solid #63b3ed' : '2px solid transparent' }}>Biological Profile</button>
        <button onClick={() => setTab('timeline')} style={{ ...st.tabBtn, backgroundColor: tab === 'timeline' ? '#2b6cb0' : '#1a202c', color: tab === 'timeline' ? '#fff' : '#a0aec0', borderBottom: tab === 'timeline' ? '2px solid #63b3ed' : '2px solid transparent' }}>Full Timeline</button>
      </div>

      {tab === 'bio' ? <BioProfileTab patientId={selectedPatient} /> : <TimelineTab patientId={selectedPatient} />}
    </div>
  );
}

// ─── Styles ───
const st: Record<string, React.CSSProperties> = {
  container: { padding: 32 },
  title: { fontSize: 24, fontWeight: 700, color: '#e2e8f0', marginBottom: 4 },
  desc: { color: '#a0aec0', fontSize: 14, marginBottom: 16, maxWidth: 700 },
  select: { backgroundColor: '#1a202c', color: '#e2e8f0', border: '1px solid #2d3748', borderRadius: 6, padding: '8px 14px', fontSize: 14, minWidth: 320, cursor: 'pointer', marginBottom: 16 },
  headerCard: { backgroundColor: '#1a202c', borderRadius: 8, padding: '16px 20px', marginBottom: 16, border: '1px solid #2d3748' },
  tabRow: { display: 'flex', gap: 0, marginBottom: 20, borderBottom: '1px solid #2d3748' },
  tabBtn: { padding: '10px 24px', border: 'none', borderRadius: '6px 6px 0 0', cursor: 'pointer', fontSize: 14, fontWeight: 600, transition: 'all 0.15s' },

  // Bio profile
  bioSection: { backgroundColor: '#1a202c', borderRadius: 8, padding: '16px 20px', marginBottom: 16, border: '1px solid #2d3748' },
  bioSectionTitle: { fontSize: 13, fontWeight: 600, color: '#cbd5e0', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 },
  bioGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 },
  metricCard: { backgroundColor: '#171923', borderRadius: 8, padding: '12px 16px', border: '1px solid #2d3748' },
  metricLabel: { color: '#718096', fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 },
  metricVal: { color: '#e2e8f0', fontSize: 18, fontWeight: 700 },
  metricDetail: { color: '#718096', fontSize: 11, marginTop: 4 },
  metricRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 },
  barBg: { height: 4, backgroundColor: '#2d3748', borderRadius: 2, marginTop: 8 },
  compositeBox: { marginTop: 16, borderRadius: 8, padding: '16px 20px' },
  compositeLabel: { color: '#a0aec0', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 },
  greenBadge: { backgroundColor: '#22543d', color: '#48bb78', padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600 },
  redBadge: { backgroundColor: '#742a2a', color: '#fc8181', padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700 },

  // Timeline controls
  controlsRow: { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12, flexWrap: 'wrap' },
  ctrlBtn: { padding: '5px 12px', backgroundColor: '#2d3748', color: '#e2e8f0', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 },
  counter: { color: '#63b3ed', fontSize: 12, fontWeight: 600, marginLeft: 8, backgroundColor: '#171923', padding: '3px 10px', borderRadius: 4 },
  filterRow: { display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' },
  filterBtn: { padding: '5px 12px', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 },

  // Timeline items
  tlHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  tlLeft: { display: 'flex', alignItems: 'center', gap: 6 },
  tlRight: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  tlCat: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 },
  tlId: { color: '#718096', fontSize: 11, fontFamily: 'monospace' },
  tlTime: { color: '#718096', fontSize: 11 },
  flagBadge: { padding: '1px 8px', borderRadius: 10, fontSize: 9, fontWeight: 700, whiteSpace: 'nowrap' },
  copyBtn: { background: 'none', border: '1px solid #2d3748', borderRadius: 4, color: '#a0aec0', fontSize: 11, cursor: 'pointer', padding: '1px 5px', lineHeight: 1 },
  xRow: { display: 'flex', gap: 8, marginBottom: 4, fontSize: 12 },
  xLabel: { color: '#a0aec0', fontWeight: 600, minWidth: 130 },
};
